import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { ReviewInterface } from 'app/assessment/models/review.model';
import { CommentsComponent } from 'app/assessment/questions/comments/comments.component';
import { DiscomfortDetailsComponent } from 'app/assessment/questions/discomfort-details/discomfort-details.component';
import { DynamicQuestionComponent } from 'app/assessment/questions/dynamic-question-content/dynamic-question.component';
import { ErgonomicRecommendationsComponent } from 'app/assessment/questions/ergonomic-recommendations/ergonomic-recommendations.component';
import { ExecutiveSummaryComponent } from 'app/assessment/questions/executive-summary/executive-summary.component';
import { MultiTextInputComponent } from 'app/assessment/questions/multi-text-input/multi-text-input.component';
import { RadioCheckboxGroupComponent } from 'app/assessment/questions/radio-checkbox-group/radio-checkbox-group.component';
import { TextInputComponent } from 'app/assessment/questions/text-input/text-input.component';
import { UnknownQuestionGroupComponent } from 'app/assessment/questions/unknown-question-group/unknown-question-group.component';
import { UserInterface } from 'app/core/user/models';
import { RecommendationInterface } from 'app/recommendation/models';
import { RecommendationCategoryInterface } from 'app/recommendation/models/recommendation-category.model';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'cz-dynamic-question-content',
  templateUrl: './dynamic-question-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicQuestionContentComponent implements OnInit, OnDestroy, AfterContentChecked {

  @ViewChild('container', { read: ViewContainerRef }) public container: ViewContainerRef;
  @Input() public readonly type: string;
  @Input() public readonly question: any;
  @Input() public readonly form: FormGroup;
  @Input() public readonly imagePath: string;
  @Input() public readonly entity: any; // AssessmentInterface, EvaluationInterface, etc.
  @Input() public readonly userId: number;
  @Input() public readonly index: number;
  @Input() public readonly currentUser: UserInterface;
  @Input() public readonly recommendationTemplates$: BehaviorSubject<Readonly<RecommendationCategoryInterface[]>>;
  @Input() public readonly readOnly: boolean;

  @Input() public readonly reviews$: BehaviorSubject<Readonly<ReviewInterface[]>>;
  @Input() public readonly recommendations$: BehaviorSubject<Readonly<RecommendationInterface[]>>;

  @Output() public recommendationUpdated: EventEmitter<RecommendationInterface> =
    new EventEmitter<RecommendationInterface>();
  @Output() public recommendationRemoved: EventEmitter<number> = new EventEmitter<number>();
  @Output() public reviewAdded: EventEmitter<ReviewInterface> = new EventEmitter<ReviewInterface>();
  @Output() public reviewUpdated: EventEmitter<ReviewInterface> = new EventEmitter<ReviewInterface>();
  @Output() public reviewRemoved: EventEmitter<ReviewInterface> = new EventEmitter<ReviewInterface>();

  // any component that could be dynamically injected needs to be in the module.entryComponents
  private _questionMappings: {[id: string]:new (dialog: MatDialog, localeId: string) => DynamicQuestionComponent } = {
    PP: RadioCheckboxGroupComponent,
    WE: RadioCheckboxGroupComponent,
    PS: RadioCheckboxGroupComponent,
    NEQ: RadioCheckboxGroupComponent,
    UA: RadioCheckboxGroupComponent,
    LATL: RadioCheckboxGroupComponent,
    LATT: RadioCheckboxGroupComponent,
    LATA: RadioCheckboxGroupComponent,
    DSES: RadioCheckboxGroupComponent,
    DSEK: RadioCheckboxGroupComponent,
    DSEC: RadioCheckboxGroupComponent,
    DSEW: RadioCheckboxGroupComponent,
    DSEP: RadioCheckboxGroupComponent,
    ECMonitorType: RadioCheckboxGroupComponent,
    ECKeyboardPlacement: RadioCheckboxGroupComponent,
    ECMousePlacement: RadioCheckboxGroupComponent,
    ECAccessories: RadioCheckboxGroupComponent,
    ECDeskType: RadioCheckboxGroupComponent,
    ESUM: ExecutiveSummaryComponent,
    IND: DiscomfortDetailsComponent,
    CL: RadioCheckboxGroupComponent,
    CT: RadioCheckboxGroupComponent,
    GLC: RadioCheckboxGroupComponent,
    WA: RadioCheckboxGroupComponent,
    CP: RadioCheckboxGroupComponent,
    EADE: RadioCheckboxGroupComponent,
    EAM: RadioCheckboxGroupComponent,
    EAKT: RadioCheckboxGroupComponent,
    EAKA: RadioCheckboxGroupComponent,
    EAC: RadioCheckboxGroupComponent,
    CBM: TextInputComponent,
    DH: MultiTextInputComponent,
    L: MultiTextInputComponent,
    C: MultiTextInputComponent,
    R: MultiTextInputComponent,
    MH: MultiTextInputComponent,
    FD: MultiTextInputComponent,
    RP: MultiTextInputComponent,
    OM: MultiTextInputComponent,
    TB: MultiTextInputComponent,
    '3-1': ErgonomicRecommendationsComponent,
    // '8-1': ErgoEvalEquipmentRecommendationsComponent,
    '14-1': CommentsComponent,
  };
  private _componentRef: ComponentRef<DynamicQuestionComponent>;

  constructor(private _componentFactoryResolver: ComponentFactoryResolver) {}

  public ngOnInit(): void {
    if (!this.type) { return; }

    const componentType: any = this.getComponentType(this.type);

    // note: componentType must be declared within module.entryComponents
    const factory: any = this._componentFactoryResolver.resolveComponentFactory(componentType);
    this._componentRef = this.container.createComponent(factory);

    // set component question, form, imagePath
    const instance: any = <DynamicQuestionComponent> this._componentRef.instance;
    instance.type = this.type;
    instance.question = this.question;
    instance.form = this.form;
    instance.imagePath = this.imagePath;
    instance.entity = this.entity;
    instance.userId = this.userId;
    instance.index = this.index;
    instance.currentUser = this.currentUser;
    instance.index = this.index;
    instance.recommendationTemplates$ = this.recommendationTemplates$;
    instance.readOnly = this.readOnly;

    instance.reviews$ = this.reviews$;
    instance.recommendations$ = this.recommendations$;

    instance.recommendationUpdated = this.recommendationUpdated;
    instance.recommendationRemoved = this.recommendationRemoved;
    instance.reviewAdded = this.reviewAdded;
    instance.reviewUpdated = this.reviewUpdated;
    instance.reviewRemoved = this.reviewRemoved;
  }

  public ngAfterContentChecked(): void {
    if (this._componentRef) {
      this._componentRef.instance.reviews$ = this.reviews$;
      this._componentRef.instance.recommendations$ = this.recommendations$;
      this._componentRef.changeDetectorRef.detectChanges();
    }
  }

  public ngOnDestroy(): void {
    if (this._componentRef) {
      this._componentRef.destroy();
      this._componentRef = null;
    }
  }

  private getComponentType(typeName: string): ComponentRef<DynamicQuestionComponent> {
    const type: any = this._questionMappings[typeName];
    return type || UnknownQuestionGroupComponent;
  }
}
